import React, { useContext, useMemo, useState } from 'react';
import * as atlas from 'azure-maps-control';
import { CompanyContext } from '../../../../../context/CompanyContext';
import { MobileStockLocationSessionContext } from '../../../../../context/MobileStockLocationSessionContext';
import { StockLocationContext } from '../../../../../context/StockLocationContext';
import { TagRelationContext } from '../../../../../context/TagRelationContext';
import { UserContext } from '../../../../../context/UserContext';
import MobileStockLocationSessionProvider from '../../../../../providers/MobileStockLocationSessionProvider';
import { MobileStockLocationSessionStatus } from '../../../../../types/mobile_stock_location';
import { TranslationKey } from '../../../../../types/translation/TranslationKey';
import { t } from '../../../../../types/translation/Translator';
import { StockLocationFilter } from '../../../../../VentoryUI/components/filters/StockLocationFilter';
import { Divider } from '../../../../components/Divider';
import { DividerOrientation } from '../../../../components/Divider/Divider';
import { SearchFilter } from '../../../../components/Filter';
import { IconButton } from '../../../../icons';
import Icon, { IconType } from '../../../../icons/Icon';
import LoadingPackage from '../../../Common/LoadingPackage';
import EndMobileStockLocationSessionModal from './EndMobileStockLocationSessionModal';
import { MobileStockLocationSessionStatusLabel } from './MobileStockLocationSessionStatusLabel';
import StockLocationSessionMap from './StockLocationSessionMap';

interface EndSessionModalProps {
  sessionId?: string;
  open: boolean;
}

interface MobileStockLocationSettingsPaneProps {}

export default function StockLocationMapPane() {
  return (
    <MobileStockLocationSessionProvider>
      <Element />
    </MobileStockLocationSessionProvider>
  );
}

function Element() {
  const { currentCompany } = useContext(CompanyContext);
  const { stockLocations, stockLocationsLoading } = useContext(StockLocationContext);
  const { tagRelations } = useContext(TagRelationContext);
  const { companyUsers } = useContext(UserContext);
  const { sessions, sessionsLoading } = useContext(MobileStockLocationSessionContext);
  const [map, setMap] = useState<atlas.Map | null>(null);

  const allItems = useMemo(() => [...stockLocations.values()], [stockLocations]);
  const [items, setItems] = useState(allItems);
  const filteredStockLocationIds = useMemo(() => new Set(items.map(i => i.id)), [items]);

  const [endSessionModalProps, setEndSessionModalProps] = useState<EndSessionModalProps>({ open: false });

  if (sessionsLoading || stockLocationsLoading) {
    return (
      <div className='flex h-full w-full items-center justify-center'>
        <LoadingPackage />
      </div>
    );
  }

  const stockLocationIdsWithSessions = new Set([...sessions.values()].map(s => s.stockLocationId));
  const stockLocationsWithoutActiveSessions = [...stockLocations.values()].filter(
    sl => !stockLocationIdsWithSessions.has(sl.id),
  );

  return (
    <div className='flex h-full flex-col gap-8 overflow-hidden'>
      {endSessionModalProps.open && endSessionModalProps.sessionId && (
        <EndMobileStockLocationSessionModal
          open={endSessionModalProps.open}
          sessionId={endSessionModalProps.sessionId}
          setOpen={open => setEndSessionModalProps({ ...endSessionModalProps, open })}
        />
      )}

      <div className='grid gap-2'>
        <p className='page-title'>{t(TranslationKey.stockLocationSettingsMapScreenTitle).singular.upper}</p>
        <p className='subtitle'>{t(TranslationKey.stockLocationSettingsMapScreenSubtitle).singular.upper}</p>
      </div>

      <div className='flex flex-grow gap-4 overflow-hidden'>
        <div className='flex h-full flex-col gap-2 overflow-hidden'>
          <SearchFilter
            filter={new StockLocationFilter(tagRelations, currentCompany)}
            items={allItems}
            setItems={setItems}
          />

          <div className='flex flex-grow flex-col gap-2 overflow-y-auto'>
            {[...sessions.values()].map(session => {
              if (!filteredStockLocationIds.has(session.stockLocationId)) return null;
              const stockLocation = stockLocations.get(session.stockLocationId);
              if (!stockLocation) return null;
              const isActive = session && session.status === MobileStockLocationSessionStatus.active;
              const coordinates = session.coordinates;

              const email = companyUsers.get(session.updatedBy)?.email ?? t(TranslationKey.unknownUser).singular.upper;
              const time = new Date(session.updatedAt).toLocaleTimeString();

              return (
                <div
                  key={session.id}
                  className='grid w-full gap-2 rounded-2 border border-gray-default px-3 py-2 shadow-sm'
                  onClick={() =>
                    map && map.setCamera({ center: [session.coordinates.longitude, session.coordinates.latitude] })
                  }
                >
                  <div className='flex items-center justify-between gap-2'>
                    <div className='flex items-center gap-2'>
                      <Icon
                        type={stockLocation.mobile ? IconType.truck1 : IconType.building3}
                        stroke={isActive ? 'stroke-success-700' : 'stroke-subtitle'}
                      />
                      <p className='subtitle'>{stockLocation.name}</p>
                    </div>
                    {coordinates && map ? (
                      <IconButton
                        onClick={() => map.setCamera({ center: [coordinates.longitude, coordinates.latitude] })}
                        type={IconType.markerPin1}
                        stroke={'stroke-subtitle'}
                      />
                    ) : null}
                  </div>

                  <p className='subtitle'>{`${
                    t(isActive ? TranslationKey.lastUpdate : TranslationKey.endedAt).singular.upper
                  }: ${time}`}</p>

                  {isActive && (
                    <div className='flex items-center justify-between gap-2'>
                      <div className='flex items-center gap-2'>
                        <MobileStockLocationSessionStatusLabel status={session.status} />
                        <p className='subtitle'>{email}</p>
                      </div>
                      <IconButton
                        type={IconType.xCircle}
                        onClick={() => setEndSessionModalProps({ open: true, sessionId: session.id })}
                        stroke={'stroke-error-500'}
                      />
                    </div>
                  )}
                </div>
              );
            })}

            {stockLocationsWithoutActiveSessions.map(stockLocation => {
              if (!filteredStockLocationIds.has(stockLocation.id)) return null;
              const coordinates = stockLocation.address?.coordinates;
              return (
                <div
                  key={stockLocation.id}
                  className='grid w-full gap-2 rounded-2 border border-gray-default px-3 py-2 shadow-sm'
                >
                  <div className='flex items-center justify-between gap-2'>
                    <div className='flex items-center gap-2'>
                      <Icon
                        type={stockLocation.mobile ? IconType.truck1 : IconType.building3}
                        stroke={'stroke-subtitle'}
                      />
                      <p className='subtitle'>{stockLocation.name}</p>
                    </div>
                    {coordinates && map ? (
                      <IconButton
                        onClick={() => map.setCamera({ center: [coordinates.longitude, coordinates.latitude] })}
                        type={IconType.markerPin1}
                        stroke={'stroke-subtitle'}
                      />
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <Divider orientation={DividerOrientation.vertical} />

        <div className={'flex-grow'}>
          <StockLocationSessionMap stockLocationIds={filteredStockLocationIds} onMapReady={map => setMap(map)} />
        </div>
      </div>
    </div>
  );
}
